import React, { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@reach/router';
import uuid from 'uuid-random';
import { Wrapper } from '../Wrapper';
import {
  MarginWrapper,
  StyledProformaButtonsWrapper,
  StyledProformaControlsWrapper,
  StyledProformaImageWrapper,
  StyledProformaLanguagesWrapper,
  StyledSliderWord,
  StyledSliderWordContainer,
  StyledWritingProforma,
} from '../Styled';
import { COLORS, EARLY } from '../../constants';
import {
  isSpaceBeforeInsertedProformaWord,
  computeSectionsColors,
  getColorByPartOfSpeech,
  getExemplarImageURL,
  getHelpText,
  getSectionColors,
  getStageColors,
} from '../../utils';
import { WritingProformaSection } from './WritingProformaSection';
import {
  AWSImage,
  ExemplarCombined,
  ImageSide,
  Proforma,
  ProformaImageSection,
  ProformaLanguageLabel,
  ProformaSideSection,
  RelatedObject,
  RelatedType,
  SectionsColors,
  WritingType,
} from '../../types';
import { ProformaContext } from '../../contexts/proformaContext';
import { AddPhotoButton, EditPhotoButton, ProformaButton } from '../Button';
import { WordsSlider } from '../WordsSlider';
import { ErrorModal, LoadingModal, NameNewModal, PreviewExemplarModal, SaveAsModal, SavedModal } from '../Modal';
import { getRedirectLink } from '../BottomNavigation/RedirectLink';
import { ReplaceImageModal } from '../ExemplarScreen/ReplaceImageModal';
import { SAVED_PROFORMA } from '../../constants/navigation';
import { SimpleImage } from '../Image/Image';
import { LeaveEditingModal } from '../Modal/LeaveEditingModal';
import { EditToolContext } from '../../contexts/editToolContext';
import useBeforeUnload from '../../hooks/useBeforeUnload';

interface IProformaExemplar {
  proforma: Proforma;
  addSavedToRelated: (relatedText: RelatedObject) => void;
}

const PROFORMAS = 'proformas';
const EXEMPLARS = 'exemplars';

export const WritingProforma: React.FC<IProformaExemplar> = (props): JSX.Element => {
  const { addSavedToRelated } = props;

  const {
    loadProforma,
    getValueById,
    handleChange,
    getImageUUID,
    handleImagePick,
    handleIsImageChanged,
    handleUpdateRelationships,
    handleSaveProformaClick,
    handleSaveExemplarClick,
    handleUpdateProforma,
    checkExemplarValue,
    setError,
    setSaved,
    setUpdated,
    getExemplarContent,
    proforma,
    isLoading,
    defaultImages,
    isSaving,
    isError,
    isSaved,
    isUpdated,
    newProforma,
    newExemplar,
    isFilled,
    isChanged,
  } = useContext(ProformaContext);
  const { isLeaveModalShown, closeLeaveModal, preventedAction, handleLeavePage } = useContext(EditToolContext);
  const { type: proformaType } = useParams();

  const sectionsColors = useMemo<SectionsColors>(
    () => computeSectionsColors(props.proforma.section_labels),
    [props.proforma.section_labels]
  );

  const [wordsSliderState, setWordsSliderState] = useState({
    partOfSpeech: '',
    isShown: false,
  });
  const [currentSection, setCurrentSection] = useState<{ id: string; current: HTMLTextAreaElement }>();
  const [isPreviewModal, setPreviewModal] = useState(false);
  const [isSaveAsModal, setSaveAsModal] = useState(false);
  const [isNewProformaModal, setNewProformaModal] = useState(false);
  const [isNewExemplarModal, setNewExemplarModal] = useState(false);
  const [isReplaceImageModalOpen, setReplaceImageModal] = useState(false);
  const [value, setValue] = useState('');
  const [retry, setRetry] = useState(PROFORMAS);
  const [keys, setKeys] = useState<string[]>([]);
  const [chosenImage, setChosenImage] = useState<{ side: ImageSide; sectionId: number }>({
    side: ImageSide.right,
    sectionId: 0,
  });
  const navigate = useNavigate();

  useBeforeUnload(e => handleLeavePage(() => navigate(window.location.href), false, e));

  const stage = proforma?.stage?.toLowerCase() || EARLY;
  const { main: mainColor, light: highlightColor } = getStageColors(stage);
  const isSavedProforma = proformaType === SAVED_PROFORMA;

  const handleImageClick = (side: ImageSide, sectionId: number) => {
    setReplaceImageModal(true);
    setChosenImage({
      side: side,
      sectionId: sectionId,
    });
  };

  const setSavedProformaImages = (sideSectionIndex: string, uuid: string) => {
    if (isSavedProforma) {
      if (getImageUUID(sideSectionIndex) === '' && uuid != '') {
        handleImagePick(sideSectionIndex, uuid);
      }
    }
  };

  const getImageStore = () => {
    return isSavedProforma && proforma ? proforma.relationships.images : defaultImages;
  };

  const handlePreviewClick = () => {
    setPreviewModal(true);
  };

  const handleSaveClick = () => {
    setSaveAsModal(true);
  };

  const handleLanguageButtonClick = (newPartOfSpeech: string) => {
    if (wordsSliderState.partOfSpeech === newPartOfSpeech) {
      setWordsSliderState({ ...wordsSliderState, isShown: !wordsSliderState.isShown });
    } else {
      setWordsSliderState({ partOfSpeech: newPartOfSpeech, isShown: true });
    }
  };

  const handleSectionFocus = (id: string) => {
    if (document.activeElement) {
      setCurrentSection({ id, current: document.activeElement as HTMLTextAreaElement });
    }
  };

  const handleWordClick = (word: string) => {
    if (!currentSection) return;

    const { id, current } = currentSection;

    if (current) {
      const { selectionStart, value, rows } = current;

      const partBeforeCursor = value.slice(0, selectionStart);
      const partAfterCursor = value.slice(selectionStart);

      const isSpaceBeforeInsertedWord = isSpaceBeforeInsertedProformaWord(partBeforeCursor);

      const updatedValue = `${partBeforeCursor}${isSpaceBeforeInsertedWord ? ' ' : ''}${word}${partAfterCursor}`;

      handleChange(id, updatedValue, rows);

      setTimeout(() => {
        current.focus();
        const caretPosition = partBeforeCursor.length + (isSpaceBeforeInsertedWord ? word.length + 1 : word.length);
        current.setSelectionRange(caretPosition, caretPosition);
      }, 1);
    }
  };

  const handleSaveProforma = (title: string) => {
    setNewProformaModal(false);

    handleSaveProformaClick(title, isSavedProforma);
  };

  const handleSaveExemplar = (title: string) => {
    setNewExemplarModal(false);

    handleSaveExemplarClick(title, isSavedProforma);

    setRetry(EXEMPLARS);
  };

  const handleSaveAsClose = () => {
    setSaveAsModal(false);
  };

  const handleNameNewClose = () => {
    if (isNewProformaModal) {
      setNewProformaModal(false);
    }
    if (isNewExemplarModal) {
      setNewExemplarModal(false);
    }
  };

  const handleSaveAsProformaClick = () => {
    setSaveAsModal(false);
    setNewProformaModal(true);
  };

  const handleSaveAsExemplarClick = () => {
    setSaveAsModal(false);
    setNewExemplarModal(true);
  };

  const handleErrorCloseClick = () => {
    setError(false);
  };

  const replaceImage = (_imageUUIDtoReplace: string, uuid: string): void => {
    const id = `${chosenImage.side}-${chosenImage.sectionId}`;
    handleIsImageChanged();
    handleImagePick(id, uuid);
  };

  const setRelationships = (data: AWSImage[]) => {
    if (!defaultImages.length) {
      const images = data.filter(image => image.url != '');
      handleUpdateRelationships(images);
    }
  };

  const closeSavedModal = (savedText: RelatedObject) => {
    !isUpdated && addSavedToRelated(savedText);
    isSaved && setSaved(false);
    isUpdated && setUpdated(false);
    setValue('');
  };

  const renderProformaSection = (sections: Array<ProformaSideSection | ProformaImageSection>, side: ImageSide) => {
    return sections.map((section: ProformaSideSection | ProformaImageSection, index) => {
      const { type, label, lines } = section as ProformaSideSection;
      const id = `${side}-${index}`;
      const colors = getSectionColors(label, sectionsColors);

      if (type === 'image') {
        const proformaSection = section as ProformaImageSection;
        setSavedProformaImages(id, proformaSection.uuid as string);
        const imageUrl = getExemplarImageURL(getImageUUID(id), getImageStore());

        return (
          <StyledProformaImageWrapper key={uuid()}>
            {imageUrl === '' ? (
              <AddPhotoButton color={mainColor} onClick={() => handleImageClick(side, index)} />
            ) : (
              <>
                <SimpleImage
                  alt=""
                  src={getExemplarImageURL(getImageUUID(id), getImageStore())}
                  width={'100%'}
                  height={'218px'}
                />
                <EditPhotoButton color={mainColor} onClick={() => handleImageClick(side, index)} />
              </>
            )}
          </StyledProformaImageWrapper>
        );
      }

      if (!keys.includes(id)) {
        setKeys([...keys, id]);
      }

      return (
        <WritingProformaSection
          data-testid="proforma-section"
          handleChange={handleChange}
          value={getValueById(id)}
          id={id}
          headerTitle={label || ''}
          titleColor={colors.title}
          mainColor={colors.text}
          key={id}
          helpText={proforma ? getHelpText(label || '', proforma?.section_labels) : ''}
          lines={(lines && lines.length) || 1}
          onFocus={handleSectionFocus}
        />
      );
    });
  };

  const renderSaveControls = () => {
    return (
      <StyledProformaControlsWrapper>
        <ProformaButton
          withoutMargin={false}
          withBorder
          inverted
          bGColor="transparent"
          text="Preview"
          onButtonClick={handlePreviewClick}
          isDisabled={!isFilled}
        />
        {isSavedProforma ? (
          <>
            <ProformaButton
              withoutMargin={false}
              withBorder
              inverted
              bGColor="transparent"
              text="Save as new"
              onButtonClick={handleSaveClick}
              isDisabled={!isChanged || !isFilled}
            />
            <ProformaButton
              withoutMargin
              inverted
              bGColor={mainColor}
              text="Save"
              onButtonClick={handleUpdateProforma}
              isDisabled={!isChanged || !isFilled}
            />
          </>
        ) : (
          <ProformaButton
            withoutMargin
            inverted
            bGColor={mainColor}
            text="Save"
            onButtonClick={handleSaveClick}
            isDisabled={!isChanged || !isFilled}
          />
        )}
      </StyledProformaControlsWrapper>
    );
  };

  const renderWordsSlider = (
    availablePartsOfSpeech: Array<ProformaLanguageLabel>,
    currentPartOfSpeech: string
  ): JSX.Element | null => {
    const current =
      availablePartsOfSpeech &&
      availablePartsOfSpeech.find(partOfSpeech => {
        return partOfSpeech.label === currentPartOfSpeech;
      });

    if (current?.words) {
      return (
        <WordsSlider partOfSpeech={currentPartOfSpeech}>
          {current.words.map((word: string, index: number) => {
            return (
              <StyledSliderWordContainer tabIndex={0} key={index + word} onClick={() => handleWordClick(word)}>
                <StyledSliderWord
                  hoverColor={getColorByPartOfSpeech(wordsSliderState.partOfSpeech)}
                  data-testid={`suggested-${currentPartOfSpeech}-word-${word}`}
                >
                  {word}
                </StyledSliderWord>
              </StyledSliderWordContainer>
            );
          })}
        </WordsSlider>
      );
    }

    return null;
  };

  const renderSavedModal = () => {
    const proformaOrExemplar: Proforma | ExemplarCombined =
      (newProforma as Proforma) || (newExemplar as ExemplarCombined);
    const { stage, text_type, id, title, page } = proformaOrExemplar;
    const type = !newExemplar ? RelatedType.saved_proformas : RelatedType.saved_exemplars;
    const link = getRedirectLink(stage, text_type, type, id, false);
    const relatedObject: RelatedObject = {
      id: id,
      textType: text_type,
      thumbnail: '',
      title,
      stage,
      type,
      page,
    };

    return (
      <SavedModal
        name={title}
        textType={text_type}
        stage={stage}
        mainColor={mainColor}
        highlightColor={highlightColor}
        isUpdated={isUpdated}
        onClose={() => closeSavedModal(relatedObject)}
        onConfirm={async () => {
          closeSavedModal(relatedObject);
          await navigate(link);
          isUpdated && loadProforma(proformaOrExemplar);
        }}
      />
    );
  };

  const renderModal = () => {
    return (
      <>
        {isPreviewModal && (
          <PreviewExemplarModal
            exemplarContent={getExemplarContent()}
            getImageStore={getImageStore}
            stage={stage}
            color={mainColor}
            onClose={() => {
              setPreviewModal(false);
            }}
          />
        )}
        {isSaveAsModal && (
          <SaveAsModal
            color={mainColor}
            isDisabled={checkExemplarValue(keys)}
            onSaveExemplarClick={handleSaveAsExemplarClick}
            onSaveProformaClick={handleSaveAsProformaClick}
            onCloseClick={handleSaveAsClose}
          />
        )}
        {isNewProformaModal && (
          <NameNewModal
            mainColor={mainColor}
            headerText="Give your Interactive Writing Proforma a title"
            value={value}
            setValue={setValue}
            onButtonClick={handleSaveProforma}
            onCloseClick={handleNameNewClose}
          />
        )}
        {isNewExemplarModal && (
          <NameNewModal
            mainColor={mainColor}
            headerText="Give your new text a title"
            value={value}
            setValue={setValue}
            onButtonClick={handleSaveExemplar}
            onCloseClick={handleNameNewClose}
          />
        )}
        {isSaving && <LoadingModal headerText={'Saving...'} color={mainColor} />}
        {isError && (
          <ErrorModal
            errorType="save"
            title={value}
            mainColor={mainColor}
            highlightColor={highlightColor}
            onRetryClick={() =>
              retry === EXEMPLARS
                ? handleSaveExemplarClick(value, isSavedProforma)
                : handleSaveProformaClick(value, isSavedProforma)
            }
            onCloseClick={handleErrorCloseClick}
          />
        )}
        {(isSaved || isUpdated) && renderSavedModal()}
      </>
    );
  };

  if (isLoading) {
    return <></>;
  }

  if (!proforma) {
    return <></>;
  }

  return (
    <>
      <Wrapper data-testid="proforma-section" color={COLORS.blackOlive}>
        {isLeaveModalShown && (
          <LeaveEditingModal
            stage={stage}
            isSavedExemplar={isSavedProforma}
            exemplarName={proforma.title}
            onClose={closeLeaveModal}
            onConfirmLeaving={() => {
              closeLeaveModal();
              loadProforma(proforma);
              preventedAction();
            }}
            onSave={() => {
              closeLeaveModal();
              handleSaveClick();
            }}
            onUpdate={() => {
              closeLeaveModal();
              handleUpdateProforma();
            }}
          />
        )}
        {isReplaceImageModalOpen && (
          <ReplaceImageModal
            exemplarId={proforma.id}
            default_uuid={undefined}
            currentImageUUID={getImageUUID(`${chosenImage.side}-${chosenImage.sectionId}`)}
            side={chosenImage.side}
            sectionId=""
            relatedImages={defaultImages}
            color={mainColor}
            replaceImage={replaceImage}
            onClose={() => setReplaceImageModal(false)}
            setRelationships={setRelationships}
            writingType={WritingType.proformas}
          />
        )}
        <StyledWritingProforma>
          <div>{renderProformaSection(proforma.content.left, ImageSide.left)}</div>
          <div>{renderProformaSection(proforma.content.right, ImageSide.right)}</div>
        </StyledWritingProforma>
        <MarginWrapper margin="0 0 -10px 0">
          <StyledProformaButtonsWrapper>
            <StyledProformaLanguagesWrapper>
              {proforma.language_feature_labels.map((label: ProformaLanguageLabel) => {
                return (
                  <ProformaButton
                    key={uuid()}
                    isLFLabel={true}
                    bGColor={getColorByPartOfSpeech(label.label)}
                    text={label.label}
                    onButtonClick={() => handleLanguageButtonClick(label.label)}
                  />
                );
              })}
            </StyledProformaLanguagesWrapper>
            {renderSaveControls()}
          </StyledProformaButtonsWrapper>
          {wordsSliderState.isShown &&
            renderWordsSlider(proforma.language_feature_labels, wordsSliderState.partOfSpeech)}
        </MarginWrapper>
      </Wrapper>
      {renderModal()}
    </>
  );
};
